import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Action } from '../../../models/core/action';
import { PendingReport } from '../../../models/api/reports/all-reports-response';
import { PendingReportAction } from '../../../models/api/reports/update-pending-reports';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { CasePipe } from '../../../pipes/case.pipe';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-pending-reports',
  imports: [
    TableComponent,
    ColumnComponent,
    MenuComponent,
    CommonModule,
    ActionButtonComponent,
    LinkLabelComponent,
    CasePipe,
    FormatPipe,
  ],
  template: `
    <div>
      <div class="tw-pt-2">
        <app-table
          [dataSource]="pendingReports()"
          recordLabel="shared report"
          dataKey="requestId"
          scrollHeight="2000px"
          [stripedRows]="false"
          [loading]="loading()"
          (refresh)="refresh()"
          sortField="copyDate"
          [sortOrder]="-1"
          [showFilterButton]="false"
          [showFilter]="false"
        >
          <app-column
            name="reportName"
            label="Name"
            filterType="select"
            [wrap]="true"
            minWidth="320px"
            maxWidth="420px"
          >
            <ng-template let-data>
              <div
                class="tw-flex tw-items-center tw-gap-2 tw-max-w-full tw-break-words tw-whitespace-normal"
              >
                <app-link-label
                  [href]="getReportDetailHref(data)"
                  (click)="viewReportDetails(data)"
                  style="cursor: pointer;"
                >
                  {{ data.reportName }}
                </app-link-label>
              </div>
            </ng-template>
          </app-column>

          <app-column name="baseReport" label="Base report" filterType="select"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.baseReport }}
              </div>
            </ng-template>
          </app-column>
          <app-column name="filterName" label="Filter" filterType="select"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.filterName }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="contract"
            label="Client"
            filterType="select"
            filterLabelName="clientName"
            filterLabelCase="titlecase"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                @if (data.contract && data.clientName) {
                  {{ data.contract }} -
                  {{ data.clientName | case: 'titlecase' }}
                }
              </div>
            </ng-template>
          </app-column>

          <app-column name="reportSender" label="Shared by" filterType="select"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.reportSender }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="copyDate"
            label="Received"
            format="dt"
            filterType="date"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.copyDate | format: 'dt' }}
              </div>
            </ng-template>
          </app-column>

          <app-column
            name="actions"
            label="Actions"
            classes=""
            [frozen]="true"
            [sort]="false"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                <app-action-button
                  [ariaLabel]="'More actions for ' + data.reportName"
                  [menu]="rowMenu"
                  [row]="data"
                  (click)="rowMenu.toggle($event, data)"
                />
              </div>
            </ng-template>
          </app-column>
        </app-table>
        <app-menu
          [items]="rowActions"
          #rowMenu
          (click)="onAction($event, rowMenu.data)"
        />
      </div>
    </div>
    @if (pendingReports().length > 0) {
      <div class="tw-pt-8 tw-italic tw-text-sm">
        Shared reports are reports that have been shared with you by another
        user. Click Accept to save shared reports to your Personalized tab.
      </div>
    }
  `,
  styles: ``,
})
export class PendingReportsComponent extends BaseComponent implements OnInit {
  private readonly snackbar = inject(Snackbar);
  private readonly reportsService = inject(ReportsService);

  readonly pendingReports = this.reportsService.pendingReports;
  readonly loading = this.reportsService.pendingLoading;

  readonly rowActions: Action[] = [
    { label: 'View details', key: 'view' },
    { label: 'Accept', key: 'accept' },
    { label: 'Decline', key: 'decline' },
  ];

  constructor() {
    super();
  }

  async ngOnInit() {
    await this.reportsService.getReports();
  }

  onAction(action: Action, report: PendingReport) {
    if (action.key === 'view') {
      this.viewReportDetails(report);
    } else if (action.key === 'accept') {
      this.acceptReport(report);
    } else if (action.key === 'decline') {
      this.confirm.show({
        label: 'Decline shared report',
        message: `Are you sure you want to decline "${report.reportName}"?`,
        width: '35rem',
        data: report,
        confirm: (data) => this.deleteReport(data),
      });
    }
  }

  async refresh() {
    await this.reportsService.getReports(true);
  }

  deleteReport(report: PendingReport) {
    this.updatePendingReport(report, 'Delete');
  }

  acceptReport(report: PendingReport) {
    this.updatePendingReport(report, 'Accept');
  }

  private async updatePendingReport(
    report: PendingReport,
    action: PendingReportAction,
  ) {
    const response = await this.reportsService.updatePendingReport(
      report.requestId,
      action,
      'JURIS',
    );

    const actionText = action === 'Accept' ? 'accepted' : 'declined';
    this.snackbar.show(
      `Shared report ${actionText}`,
      'Success',
      'fa-light fa-file-lines',
      3000,
    );

    if (this.reportsService.pendingReports().length === 0) {
      this.router.navigate(['/reports'], {
        queryParams: { tab: 'reports' },
      });
    }

    if (response.emails && response.emails.length > 0) {
      const emailText = response.emails.length === 1 ? 'email' : 'emails';
      this.snackbar.show(
        `Notification ${emailText} sent`,
        'Info',
        'fa-light fa-envelope',
        2000,
      );
    }
  }

  getReportDetailHref(report: PendingReport): string {
    return this.reportsService.getDetailUrl(report, 'pending');
  }

  viewReportDetails(report: PendingReport) {
    this.reportsService.navigateToDetail(report, 'pending');
  }
}
